import { constant, isFunction, isString } from 'lodash';
import {
  ExtendedError,
  extendedError,
  isObjectWithProperty,
  isObjectWithStringProperty,
} from '@common/shared/util';
import { modules } from '@veritone/glc-redux';
import {
  ActionCreatorWithPreparedPayload,
  createAction,
  UnknownAction,
} from '@reduxjs/toolkit';

const { auth: authModule } = modules;

const {
  config: { getConfig },
  user: { logout },
} = modules;

// type ActionType<T = any> =
//   | string
//   | ActionCreatorWithPayload<T, string>
//   | ActionCreatorWithoutPayload<string>;

type ApiFailureType<T = any> = ActionCreatorWithPreparedPayload<
  any[],
  T,
  string,
  boolean | undefined,
  any
>;
type ApiSuccessType<T> = ActionCreatorWithPreparedPayload<
  any[],
  T,
  string,
  boolean | undefined,
  any
>;
type ApiRequestType = ActionCreatorWithPreparedPayload<
  any[],
  undefined,
  string,
  never,
  any
>;

interface ApiMetadata<V> {
  variables: V;
  operationName?: string;
  query?: string;
}
// TODO: Should probably pass payload first, then meta
export function createApiRequestAction<V = Record<string, any>>(type: string) {
  return createAction(type, (meta: ApiMetadata<V>) => ({
    payload: undefined,
    meta,
  }));
}

export function createApiSuccessAction<P, V = Record<string, any>>(
  type: string
) {
  return createAction(
    type,
    (payload: P, meta: ApiMetadata<V>, error?: false) => ({
      payload,
      meta,
      error,
    })
  );
}

export function createApiFailureAction<E = any, V = Record<string, any>>(
  type: string
) {
  return createAction(
    type,
    (payload: E, meta: ApiMetadata<V>, error?: boolean) => ({
      payload,
      meta,
      error,
    })
  );
}

export default async function callApi<T>({
  actionTypes, // : [requestType, successType, failureType],
  query,
  body: _body,
  variables,
  operationName,
  bailout: _bailout = constant(false),
  dispatch,
  getState,
  file,
  url,
}: {
  // actionTypes: [ActionType, ActionType, ActionType];
  actionTypes: Readonly<
    [ApiRequestType | string, ApiSuccessType<T>, ApiFailureType]
  >;
  query?: string;
  body?: string | FormData;
  variables: Record<string, any>;
  operationName?: string;
  bailout?: () => boolean;
  dispatch?: (v: any) => any;
  getState?: () => any;
  file?: BlobPart;
  url?: string;
}) {
  if (!isFunction(dispatch) || !isFunction(getState)) {
    throw new Error('callApi requires dispatch and getState functions');
  }

  const [requestType, successType, failureType] = actionTypes;

  // TODO: Can we type the return of getState here?
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const state = getState();
  const config = getConfig(state);
  const token = authModule.selectSessionToken(state);
  const endpoint = `${config.apiRoot}/${config.graphQLEndpoint}`;

  const meta = { variables, operationName, query };
  if (isString(requestType)) {
    dispatch({
      type: requestType,
      meta,
    });
  } else {
    dispatch(requestType(meta));
  }

  let response:
    | {
        data?: T;
        status: number;
        errors?: unknown[];
      }
    | ExtendedError;

  if (file) {
    // TODO: make multiPartRequest generic so results are typed
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    response = await multiPartRequest(
      endpoint,
      token,
      query,
      variables,
      operationName,
      dispatch,
      failureType,
      file
    );
  } else if (url) {
    // TODO: make jsonDownload generic so results are typed
    response = await jsonDownload<T>(url, dispatch, failureType);
  } else {
    throw new Error('Unexpected parameters set');
  }

  // @ts-expect-error TODO: Implement type guards to validate response type
  if (!response.data || response.errors) {
    dispatch(
      failureType(response.errors, { variables, operationName, query }, true)
    );
    // dispatch({
    //   type: failureType,
    //   error: true,
    //   payload: response.errors,
    //   meta: { variables, operationName, query },
    // });

    // TODO: Fix response.errors type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const errorAuth = response.errors?.find(
      (item: any) => item.name === 'authentication_error'
    );
    if (errorAuth) {
      dispatch(logout());
    }

    return extendedError(
      // @ts-expect-error TODO: Is errors guaranteed to be Error[]?
      `Status ${response.status}: ${response.errors?.[0].message}`,
      response.errors as any
    );
  } else {
    // dispatch({
    //   type: successType,
    //   payload: response.data,
    //   meta: { variables, operationName, query },
    // });
    // @ts-expect-error TODO: Fix types
    dispatch(successType(response.data, { variables, operationName, query }));

    // @ts-expect-error TODO: Fix types
    return response.data as T;
  }
}

async function jsonDownload<T>(
  url: string,
  dispatch: (v: UnknownAction) => void,
  failureType: ApiFailureType
) {
  try {
    return await fetch(url, {
      method: 'get',
    }).then(async (r) => {
      const { status } = r;
      try {
        const response = (await r.json()) as T;
        return { data: { ...response }, status };
      } catch (e) {
        if (isObjectWithProperty(e, 'message')) {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          e.message = `Status ${status}: ${e.message}`;
        }
        throw e;
      }
    });
  } catch (e) {
    // dispatch({
    //   type: failureType,
    //   error: true,
    //   payload: e,
    // });
    dispatch(failureType(e, undefined, true));

    return extendedError(
      isObjectWithStringProperty(e, 'message') ? e.message : 'Unknown Error',
      [e]
    );
  }
}

async function multiPartRequest(
  endpoint: string,
  token: string | null,
  query: string | undefined,
  variables: Record<string, any>,
  operationName: string | undefined,
  dispatch: (v: any) => void,
  // failureType: string,
  failureType: ApiFailureType,
  file: BlobPart
) {
  const blob = new Blob([file], { type: 'application/octet-binary' });
  try {
    const formData = new FormData();

    if (query) {
      formData.append('query', query);
    }
    formData.append('variables', JSON.stringify(variables));
    formData.append('filename', 'udrs.json');
    formData.append('file', blob, 'udrs.json');

    // Make function generic and type results
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return await fetch(endpoint, {
      method: 'post',
      body: formData,
      // @ts-expect-error: Technically null not allowed by type
      headers: {
        Authorization: token ? `Bearer ${token}` : null,
      },
    }).then(async (r) => {
      const { status } = r;
      try {
        // TODO: Make function generic and type results
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const response = await r.json();
        // TODO: Make function generic and type results
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return { ...response, status };
      } catch (e) {
        if (isObjectWithStringProperty(e, 'message')) {
          e.message = `Status ${status}: ${e.message}`;
        }
        throw e;
      }
    });
  } catch (e) {
    if (isString(failureType)) {
      dispatch({
        type: failureType,
        error: true,
        payload: e,
        meta: { variables, operationName, query },
      });
    } else {
      dispatch(failureType(e, { variables, operationName, query }, true));
    }
    return extendedError(
      isObjectWithStringProperty(e, 'message') ? e.message : 'Unknown Error',
      [e]
    );
  }
}

export const fetchGraphQLApi = async function <T>({
  endpoint = 'https://api.veritone.com/v3/graphql',
  query,
  variables,
  operationName,
  token,
  veritoneAppId,
  keepAlive,
}: {
  endpoint: string;
  query: string;
  variables: Record<string, any>;
  operationName?: string | null;
  token?: string | null;
  veritoneAppId: string;
  keepAlive?: boolean;
}) {
  const headers: Record<string, any> = {
    Authorization: token ? `Bearer ${token}` : null,
    'Content-Type': 'application/json',
  };
  if (veritoneAppId) {
    headers['x-veritone-application'] = veritoneAppId;
  }
  return await fetch(endpoint, {
    method: 'post',
    keepalive: keepAlive ?? false,
    body: JSON.stringify({
      query,
      variables,
      operationName,
    }),
    headers,
  }).then(async (r) => {
    const { status } = r;
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response: { data?: T; errors?: any[] } = await r.json();
      return { ...response, status };
    } catch (e) {
      if (isObjectWithStringProperty(e, 'message')) {
        e.message = `Status ${status}: ${e.message}`;
      }
      throw e;
    }
  });
};
